import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { getClients, createClient, updateClient, deleteClient, getUnreadCounts, sendClientInvite, getGroups, getGroup, updateGroupClients, generateAccessCode, checkClientEmail, getOfferings, setClientOfferings, PORTAL_BASE_URL } from '../api/client';
import LoadingLeaf from '../components/LoadingLeaf';
import OfferingAddPicker from '../components/OfferingAddPicker';
import OfferingFormModal from '../components/OfferingFormModal';

export default function Clients() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(searchParams.get('new') === '1');
  const [form, setForm] = useState({ name: '', email: '' });
  const [offerings, setOfferings] = useState([]);
  const [selectedOfferingIds, setSelectedOfferingIds] = useState([]);
  const [showOfferingModal, setShowOfferingModal] = useState(false);
  const [groups, setGroups] = useState([]);
  const [addToGroupId, setAddToGroupId] = useState('');
  const [generatedLink, setGeneratedLink] = useState('');
  const [generatedClient, setGeneratedClient] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchClients(); fetchUnreadCounts(); fetchGroups();
    getOfferings().then(res => setOfferings(res.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (searchParams.get('new') === '1') setSearchParams({}, { replace: true });
  }, []);

  const fetchClients = async () => {
    try {
      const clientsRes = await getClients();
      setClients(clientsRes.data);
    } catch (err) {
      setError('Could not load clients.');
    } finally {
      setLoading(false);
    }
  };

  // Kept independent from fetchClients — unread counts are a secondary detail, and
  // Promise.all-ing them together meant a hiccup on this call alone (e.g. a slower
  // cold-start response, common right after a page refresh) wiped out a perfectly
  // good client list, since Promise.all rejects the whole batch if either call fails.
  const fetchUnreadCounts = async () => {
    try {
      const unreadRes = await getUnreadCounts();
      setUnreadCounts(unreadRes.data || {});
    } catch (err) {
      // non-critical — unread badges just won't show this load
    }
  };

  const fetchGroups = async () => {
    try {
      const res = await getGroups();
      setGroups(res.data || []);
    } catch (err) {
      // Non-critical — Add Client still works without the group picker.
    }
  };

  const resetModal = () => {
    setShowModal(false);
    setForm({ name: '', email: '' });
    setSelectedOfferingIds([]);
    setAddToGroupId('');
    setGeneratedLink('');
    setGeneratedClient(null);
    setLinkCopied(false);
  };

  const showToast = (msg) => {
    const el = document.createElement('div');
    el.textContent = msg;
    el.style.cssText = 'position:fixed;bottom:24px;right:24px;background:var(--forest);color:white;padding:12px 20px;border-radius:10px;font-size:13.5px;z-index:9999;font-family:DM Sans,sans-serif;box-shadow:0 4px 16px rgba(0,0,0,0.2)';
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 2500);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const email = form.email.trim();
    if (email) {
      // Same soft duplicate warning as the invite screen — scoped to this
      // coach's own clients only.
      try {
        const checkRes = await checkClientEmail(email);
        const match = checkRes.data.client;
        if (match && !window.confirm(`You already have a client named "${match.name}" with this email. Use this email anyway?`)) {
          return;
        }
      } catch (err) {
        // non-critical — if the check itself fails, don't block creating the client
      }
    }
    setSaving(true);
    try {
      // Backend still requires session_type on creation; the coach is no longer
      // asked for it here, so we send a fixed default.
      const res = await createClient({ name: form.name, email: email || undefined, session_type: 'Life coaching' });
      let newClient = { ...res.data, offerings: [] };

      if (selectedOfferingIds.length > 0) {
        try {
          const offRes = await setClientOfferings(newClient.id, selectedOfferingIds);
          newClient = { ...newClient, offerings: offRes.data || [] };
        } catch (offErr) {
          setError('Client was created, but could not be linked to the offering.');
        }
      }
      setClients([...clients, newClient]);
      setGeneratedClient(newClient);

      if (addToGroupId) {
        try {
          const groupRes = await getGroup(addToGroupId);
          const existingIds = (groupRes.data.clients || []).map(c => c.id);
          await updateGroupClients(addToGroupId, [...existingIds, res.data.id]);
        } catch (groupErr) {
          setError('Client was created, but could not be added to the group.');
        }
      }

      try {
        const linkRes = await generateAccessCode(res.data.id);
        setGeneratedLink(`${PORTAL_BASE_URL}/portal/${linkRes.data.access_code}`);
      } catch (linkErr) {
        setError('Client was created, but the portal link could not be generated.');
        resetModal();
      }
    } catch (err) {
      setError('Could not save client.');
    } finally {
      setSaving(false);
    }
  };

  const handleSendInviteEmail = async () => {
    if (!generatedClient || !form.email.trim()) return;
    const trimmedEmail = form.email.trim();
    if (trimmedEmail !== (generatedClient.email || '')) {
      try {
        const checkRes = await checkClientEmail(trimmedEmail, generatedClient.id);
        const match = checkRes.data.client;
        if (match && !window.confirm(`You already have a client named "${match.name}" with this email. Use this email anyway?`)) {
          return;
        }
      } catch (err) {
        // non-critical — if the check itself fails, don't block sending the invite
      }
    }
    setSendingInvite(true);
    try {
      if (trimmedEmail !== (generatedClient.email || '')) {
        await updateClient(generatedClient.id, { ...generatedClient, email: trimmedEmail });
      }
      await sendClientInvite(generatedClient.id);
      showToast('Email sent');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not send the invite email.');
    } finally {
      setSendingInvite(false);
    }
  };

  const handleDelete = (client, e) => {
    e.stopPropagation();
    setDeleteTarget(client);
    setDeleteConfirmText('');
  };

  const confirmDeleteClient = async () => {
    if (deleteConfirmText !== 'DELETE' || !deleteTarget) return;
    setDeleting(true);
    try {
      await deleteClient(deleteTarget.id);
      setClients(prev => prev.filter(c => c.id !== deleteTarget.id));
      setDeleteTarget(null);
      setDeleteConfirmText('');
    } catch (err) {
      setError('Could not delete client.');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const filtered = clients.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.session_type?.toLowerCase().includes(search.toLowerCase())
  );

  const totalUnread = Object.values(unreadCounts).reduce((a, b) => a + b, 0);

  return (
    <div style={styles.page}>

      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={styles.pageTitle}>Clients</div>
          <div style={styles.pageSubtitle}>
            {clients.length} client{clients.length !== 1 ? 's' : ''}
            {totalUnread > 0 && <span style={styles.unreadBadge}>{totalUnread} unread</span>}
          </div>
        </div>
        <button style={styles.addBtn} onClick={() => setShowModal(true)}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><line x1="8" y1="2" x2="8" y2="14" /><line x1="2" y1="8" x2="14" y2="8" /></svg>
          Add client
        </button>
      </div>

      {error && <div style={styles.errorAlert}>{error}</div>}

      {/* Search */}
      {clients.length > 3 && (
        <div style={styles.searchWrap}>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={styles.searchIcon}><circle cx="6.5" cy="6.5" r="4.5" /><path d="M10.5 10.5l3 3" /></svg>
          <input
            style={styles.searchInput}
            placeholder="Search clients..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      )}

      {/* Client grid */}
      {loading ? (
        <div style={styles.loadingState}><LoadingLeaf size={48} label="Loading clients…" /></div>
      ) : filtered.length === 0 && clients.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>👤</div>
          <h3 style={styles.emptyTitle}>No clients yet</h3>
          <p style={styles.emptyText}>Add your first client to get started.</p>
          <button style={styles.emptyBtn} onClick={() => setShowModal(true)}>Add client</button>
        </div>
      ) : filtered.length === 0 ? (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>No clients match "{search}"</p>
        </div>
      ) : (
        <div style={styles.clientGrid}>
          {filtered.map(client => {
            const initials = client.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
            const unread = unreadCounts[client.id] || 0;
            return (
              <div
                key={client.id}
                style={styles.clientCard}
                className="tc-card-hover"
                onClick={() => navigate(`/clients/${client.id}`)}
              >
                <div style={styles.clientCardTop}>
                  <div style={styles.clientAvatar}>{initials}</div>
                  <div style={styles.clientCardActions}>
                    {unread > 0 && (
                      <span style={styles.unreadPill}>{unread} unread</span>
                    )}
                    <button
                      style={styles.deleteBtn}
                      onClick={(e) => handleDelete(client, e)}
                      title="Delete client"
                    >
                      ×
                    </button>
                  </div>
                </div>
                <div style={styles.clientName}>{client.name}</div>
                <div style={styles.clientType}>{client.session_type}</div>
                {client.modality && (
                  <div style={styles.clientModality}>{client.modality}</div>
                )}
                <div style={styles.clientCardFooter}>
                  <span style={styles.viewProfile}>View profile →</span>
                </div>
              </div>
            );
          })}

          {/* Add client card */}
          <div
            style={styles.addCard}
            onClick={() => setShowModal(true)}
          >
            <div style={styles.addCardIcon}>+</div>
            <div style={styles.addCardLabel}>Add client</div>
          </div>
        </div>
      )}

      {/* Add Client Modal */}
      {showModal && (
        <div style={styles.modalOverlay} onClick={resetModal}>
          <div style={generatedLink ? styles.inviteCard : styles.modal} onClick={e => e.stopPropagation()}>
            {generatedLink ? (
              <>
                <div style={styles.inviteHeader}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={styles.inviteAvatar}>
                      {(generatedClient?.name || form.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={styles.inviteTitle}>
                        Invite {generatedClient?.name || form.name || 'them'} to their portal
                      </div>
                    </div>
                  </div>
                  <button style={styles.inviteClose} onClick={resetModal}>×</button>
                </div>

                <div style={styles.inviteSubtitle}>
                  They'll set up their own profile and password — nothing else to enter for them.
                </div>

                <div style={styles.inviteBody}>
                  <div style={styles.inviteCaption}>Their portal link</div>
                  <div style={styles.inviteLinkRow}>
                    <div style={styles.inviteLinkBox}>{generatedLink}</div>
                    <button
                      style={styles.inviteCopyBtn}
                      onClick={() => {
                        navigator.clipboard.writeText(generatedLink);
                        showToast('Link copied');
                        setLinkCopied(true);
                        setTimeout(() => setLinkCopied(false), 2000);
                      }}
                    >
                      {linkCopied ? '✓ Copied' : '⧉ Copy'}
                    </button>
                  </div>

                  <div style={styles.inviteDivider}>
                    <span style={styles.inviteDividerLine} />
                    <span style={styles.inviteDividerText}>or send it for them</span>
                    <span style={styles.inviteDividerLine} />
                  </div>

                  <div style={styles.inviteLinkRow}>
                    <input
                      type="email"
                      style={styles.inviteEmailInput}
                      value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      placeholder="client@example.com"
                    />
                    <button
                      style={styles.inviteCopyBtn}
                      onClick={handleSendInviteEmail}
                      disabled={sendingInvite || !form.email.trim()}
                    >
                      {sendingInvite ? 'Sending...' : 'Email it'}
                    </button>
                  </div>
                  <div style={styles.inviteFinePrint}>Sends a branded invite from your account.</div>
                </div>

                <div style={styles.inviteFooter}>
                  🕐 Track their portal setup anytime under{' '}
                  <Link to={`/clients/${generatedClient?.id}`} style={styles.inviteFooterLink} onClick={resetModal}>
                    Clients › {generatedClient?.name || form.name}
                  </Link>
                </div>
              </>
            ) : (
              <>
                <div style={styles.modalHeader}>
                  <div style={styles.modalTitle}>New client</div>
                  <button style={styles.modalClose} onClick={resetModal}>×</button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Full name</label>
                    <input
                      style={styles.input}
                      value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })}
                      placeholder="e.g. Morgan Lee"
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Email <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
                    <input
                      type="email"
                      style={styles.input}
                      value={form.email}
                      onChange={e => setForm({ ...form, email: e.target.value })}
                      placeholder="jane@email.com"
                    />
                  </div>
                  <div style={styles.offeringBox}>
                    <div style={styles.offeringBoxTitle}>Link to offering <span style={styles.offeringBoxOptional}>· optional</span></div>
                    <div style={styles.offeringBoxHint}>Links their sessions into that offering's pool. Saving a session under an offering links them automatically too.</div>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                      {selectedOfferingIds.map(oid => {
                        const o = offerings.find(x => x.id === oid);
                        if (!o) return null;
                        return (
                          <span key={oid} style={styles.offeringChip}>
                            {o.name}
                            <span style={{ cursor: 'pointer', opacity: 0.7 }} onClick={() => setSelectedOfferingIds(ids => ids.filter(x => x !== oid))}>×</span>
                          </span>
                        );
                      })}
                      <OfferingAddPicker
                        offerings={offerings}
                        selectedOfferingIds={selectedOfferingIds}
                        onToggle={oid => setSelectedOfferingIds(ids => ids.includes(oid) ? ids.filter(x => x !== oid) : [...ids, oid])}
                        onCreateNew={() => setShowOfferingModal(true)}
                      />
                    </div>
                  </div>
                  {groups.length > 0 && (
                    <div style={styles.formGroup}>
                      <label style={styles.label}>Link to group <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
                      <select
                        style={styles.input}
                        value={addToGroupId}
                        onChange={e => setAddToGroupId(e.target.value)}
                      >
                        <option value="">— No group —</option>
                        {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                      </select>
                    </div>
                  )}
                  <div style={styles.notice}>
                    🔒 Client data is stored securely and privately in your account only.
                  </div>
                  <div style={styles.modalFooter}>
                    <button type="button" style={styles.cancelBtn} onClick={resetModal}>
                      Cancel
                    </button>
                    <button type="submit" style={styles.saveBtn} disabled={saving}>
                      {saving ? 'Saving...' : 'Create client'}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Client Modal */}
      {deleteTarget && (
        <div style={styles.modalOverlay} onClick={() => setDeleteTarget(null)}>
          <div style={styles.deleteModal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.deleteModalTitle}>Delete {deleteTarget.name}?</h3>
            <p style={styles.deleteModalBody}>
              This will permanently delete {deleteTarget.name} and everything tied to them — sessions, goals, and all past conversations. <strong>This cannot be undone.</strong>
            </p>
            <p style={styles.deleteModalPrompt}>Type <strong>DELETE</strong> to confirm:</p>
            <input
              style={{ ...styles.input, borderColor: deleteConfirmText === 'DELETE' ? 'var(--red)' : 'var(--border)', marginBottom: '20px' }}
              value={deleteConfirmText}
              onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder="DELETE"
              autoFocus
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                style={{ ...styles.deleteConfirmBtn, opacity: deleteConfirmText === 'DELETE' && !deleting ? 1 : 0.4 }}
                onClick={confirmDeleteClient}
                disabled={deleteConfirmText !== 'DELETE' || deleting}
              >
                {deleting ? 'Deleting...' : `Yes, delete ${deleteTarget.name}`}
              </button>
              <button style={styles.cancelBtn} onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rendered outside the New client modal so clicks on its overlay can't
          bubble up and close that modal too. */}
      <OfferingFormModal
        open={showOfferingModal}
        onClose={() => setShowOfferingModal(false)}
        existingOffering={null}
        onSaved={(o) => {
          setOfferings(prev => [...prev, o]);
          setSelectedOfferingIds(ids => [...ids, o.id]);
          setShowOfferingModal(false);
        }}
      />
    </div>
  );
}

const styles = {
  offeringBox: { background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px' },
  offeringBoxTitle: { fontSize: '13px', fontWeight: '600', color: 'var(--gold)' },
  offeringBoxOptional: { fontWeight: '400', color: 'var(--text-soft)' },
  offeringBoxHint: { fontSize: '12px', color: 'var(--text-soft)', margin: '2px 0 10px' },
  offeringChip: { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'white', border: '1px solid var(--border)', borderRadius: '20px', padding: '5px 12px', fontSize: '12.5px', color: 'var(--text-dark)' },
  page: { padding: '36px 40px', fontFamily: "'DM Sans', sans-serif" },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '28px' },
  pageTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '28px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '4px' },
  pageSubtitle: { fontSize: '13px', color: 'var(--text-soft)', display: 'flex', alignItems: 'center', gap: '10px' },
  unreadBadge: { background: 'var(--red-light)', color: 'var(--red)', border: '1px solid rgba(194,94,94,0.25)', borderRadius: '20px', fontSize: '11px', fontWeight: '600', padding: '2px 9px' },
  addBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 20px', borderRadius: '10px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  searchWrap: { position: 'relative', marginBottom: '24px', maxWidth: '320px' },
  searchIcon: { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-faint)' },
  searchInput: { width: '100%', padding: '10px 14px 10px 36px', borderRadius: '10px', border: '1px solid var(--border)', background: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box' },
  clientGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '16px' },
  clientCard: { background: 'white', borderRadius: '14px', border: '1px solid var(--border)', padding: '20px', cursor: 'pointer', boxShadow: 'var(--shadow-sm)', display: 'flex', flexDirection: 'column' },
  clientCardTop: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '14px' },
  clientAvatar: { width: '44px', height: '44px', borderRadius: '50%', background: 'var(--mint)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: '600', color: 'var(--sage)' },
  clientCardActions: { display: 'flex', alignItems: 'center', gap: '8px' },
  unreadPill: { background: 'var(--red-light)', color: 'var(--red)', border: '1px solid rgba(194,94,94,0.25)', borderRadius: '20px', fontSize: '11px', fontWeight: '600', padding: '2px 8px' },
  deleteBtn: { background: 'none', border: 'none', color: 'var(--text-faint)', fontSize: '20px', cursor: 'pointer', lineHeight: 1, padding: '0 2px' },
  clientName: { fontFamily: "'Lora', Georgia, serif", fontSize: '17px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '4px' },
  clientType: { fontSize: '12.5px', color: 'var(--text-soft)', marginBottom: '4px' },
  clientModality: { display: 'inline-block', background: 'var(--mint)', color: 'var(--sage)', borderRadius: '20px', fontSize: '11px', fontWeight: '500', padding: '3px 10px', marginTop: '4px' },
  clientCardFooter: { marginTop: 'auto', paddingTop: '16px' },
  viewProfile: { fontSize: '12.5px', color: 'var(--sage)', fontWeight: '500' },
  addCard: { background: 'none', borderRadius: '14px', border: '2px dashed var(--border)', padding: '20px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '160px', transition: 'border-color 0.15s, background 0.15s', gap: '8px' },
  addCardIcon: { width: '40px', height: '40px', borderRadius: '50%', background: 'var(--mint)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', color: 'var(--sage)', fontWeight: '300' },
  addCardLabel: { fontSize: '13.5px', color: 'var(--text-soft)', fontWeight: '500' },
  loadingState: { padding: '60px', textAlign: 'center', color: 'var(--text-soft)', fontSize: '14px' },
  emptyState: { textAlign: 'center', padding: '80px 24px', color: 'var(--text-soft)' },
  emptyIcon: { fontSize: '40px', marginBottom: '16px' },
  emptyTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '22px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '8px' },
  emptyText: { fontSize: '14px', marginBottom: '20px' },
  emptyBtn: { padding: '11px 22px', borderRadius: '10px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', cursor: 'pointer' },
  errorAlert: { background: 'var(--red-light)', color: 'var(--red)', padding: '12px 16px', borderRadius: '8px', fontSize: '13.5px', marginBottom: '16px' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(26,46,40,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, backdropFilter: 'blur(2px)' },
  modal: { background: 'white', borderRadius: '16px', width: '560px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)' },
  modalHeader: { padding: '24px 28px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' },
  modalTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '22px', fontWeight: '400', color: 'var(--text-dark)' },
  modalClose: { background: 'none', border: 'none', fontSize: '24px', color: 'var(--text-faint)', cursor: 'pointer', lineHeight: 1 },
  formGroup: { marginBottom: '18px', padding: '0 28px' },
  label: { display: 'block', fontSize: '11.5px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text-soft)', marginBottom: '8px' },
  input: { width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--cream)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '11px 14px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--cream)', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', color: 'var(--text-dark)', outline: 'none', boxSizing: 'border-box', minHeight: '90px', resize: 'vertical' },
  notice: { margin: '0 28px 16px', background: 'var(--mint)', color: 'var(--sage)', padding: '12px 16px', borderRadius: '8px', fontSize: '12.5px' },
  modalFooter: { display: 'flex', gap: '10px', justifyContent: 'flex-end', padding: '16px 28px 24px', borderTop: '1px solid var(--border-light)' },
  cancelBtn: { padding: '10px 18px', borderRadius: '8px', background: 'none', border: '1px solid var(--border)', color: 'var(--text-mid)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer' },
  saveBtn: { padding: '10px 20px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer', fontWeight: '500' },

  // Delete-client confirmation modal
  deleteModal: { background: 'white', borderRadius: '14px', padding: '32px', maxWidth: '420px', width: '100%', boxShadow: 'var(--shadow-lg)' },
  deleteModalTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '20px', fontWeight: '600', color: 'var(--red)', margin: '0 0 12px' },
  deleteModalBody: { fontSize: '14px', color: 'var(--text-mid)', lineHeight: 1.6, margin: '0 0 20px' },
  deleteModalPrompt: { fontSize: '13px', color: 'var(--text-mid)', marginBottom: '8px' },
  deleteConfirmBtn: { padding: '11px 20px', borderRadius: '8px', background: 'var(--red)', border: 'none', color: '#fff', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: '600', cursor: 'pointer', flex: 1 },

  // Post-creation invite screen — dark, built from the app's existing --forest/
  // --cream/--gold tokens (there's no other dark-mode surface in the app besides
  // the sidebar, which uses --forest the same way).
  inviteCard: { background: 'var(--forest)', borderRadius: '16px', width: '480px', maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-lg)', padding: '28px' },
  inviteHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
  inviteAvatar: { width: '40px', height: '40px', borderRadius: '50%', background: 'var(--gold)', color: 'var(--forest)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Lora', Georgia, serif", fontSize: '17px', fontWeight: '600', flexShrink: 0 },
  inviteTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '19px', fontWeight: '400', color: 'white', lineHeight: 1.3 },
  inviteClose: { background: 'none', border: 'none', fontSize: '22px', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', lineHeight: 1 },
  inviteSubtitle: { fontSize: '13.5px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.6, margin: '14px 0 24px' },
  inviteBody: {},
  inviteCaption: { fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.5)', marginBottom: '8px' },
  inviteLinkRow: { display: 'flex', gap: '10px' },
  inviteLinkBox: { flex: 1, padding: '11px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', fontFamily: 'monospace', fontSize: '13px', color: 'var(--cream)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center' },
  inviteEmailInput: { flex: 1, padding: '11px 14px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', color: 'var(--cream)', outline: 'none', boxSizing: 'border-box' },
  inviteCopyBtn: { padding: '11px 18px', borderRadius: '8px', background: 'var(--cream)', border: 'none', color: 'var(--forest)', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap' },
  inviteDivider: { display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0' },
  inviteDividerLine: { flex: 1, height: '1px', background: 'rgba(255,255,255,0.15)' },
  inviteDividerText: { fontSize: '12px', color: 'rgba(255,255,255,0.45)' },
  inviteFinePrint: { fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginTop: '10px' },
  inviteFooter: { marginTop: '24px', paddingTop: '18px', borderTop: '1px solid rgba(255,255,255,0.12)', fontSize: '12.5px', color: 'rgba(255,255,255,0.55)' },
  inviteFooterLink: { color: 'var(--cream)', fontWeight: '600', textDecoration: 'none' },
};